import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { PAGE_START } from '~/common/constants/page';
import AlertsFilters from '~/components/AlertsPage/AlertsFilters/AlertsFilters';
import AlertsList from '~/components/AlertsPage/AlertsList/AlertsList';
import { getAlerts } from '~/components/AlertsPage/AlertsPage.data';
import EcModal from '~/components/Shared/EcModal/EcModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import withSideCollapse from '~/components/Shared/HOCs/withSideCollapse/withSideCollapse';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { QueryParamType } from '~/enums/QueryParamType';
import { AddAlertPanel } from '~/features/alerts/AddAlertPanel';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { api } from '~/redux/api/createApi';
import { createAlert } from '~/redux/api/methods/alerts/createAlert';
import { MODAL_ALERT } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';

const AlertFiltersWithSideCollapse = withSideCollapse(AlertsFilters);

class Page extends Component {
  constructor(props) {
    super(props);

    this.loadAlerts = this.loadAlerts.bind(this);
    this.onItemCreate = this.onItemCreate.bind(this);
    this.onItemUpdate = this.onItemUpdate.bind(this);
    this.handleOnPageChange = this.handleOnPageChange.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleOnFiltersChange = this.handleOnFiltersChange.bind(this);
    this.handleDeleteItems = this.handleDeleteItems.bind(this);
    this.handleUpdateItem = this.handleUpdateItem.bind(this);

    this.state = {
      alerts: [],
      listLoading: true,
      listError: false,
      page: 1,
      pageCount: 1,
      filteredResultsCount: null,
      searchQueries: null,
      currentModal: null,
    };
  }

  componentDidMount() {
    const page = getPageSearchQueryByKey(QueryParamType.Page, PAGE_START);
    this.setState({ page }, () => {
      this.loadAlerts();
      updatePageSearchQuery({
        [QueryParamType.Page]: page,
      });
    });
  }

  loadAlerts() {
    const { page, searchQueries } = this.state;
    this.setState({ listLoading: true, listError: false });

    getAlerts(page, searchQueries)
      .then(({ results, count }) =>
        this.setState({
          alerts: results,
          pageCount: count,
          listLoading: false,
        }),
      )
      .catch(() => this.setState({ listLoading: false, listError: true }));
  }

  onItemCreate(data) {
    createAlert(data)
      .then((res) => {
        this.setState({ page: PAGE_START }, () => {
          this.loadAlerts();
          updatePageSearchQuery({
            [QueryParamType.Page]: PAGE_START,
          });
        });
        showToast(
          SUCCESS,
          this.props.intl.formatMessage(
            {
              id: 'AlertsPage.alertCreatedMessage',
              defaultMessage: 'Alert "{name}" has been created!',
              description:
                'Success message when alert is created. Example: name = "Contract Expiration Alert"',
            },
            {
              name: res.name,
            },
          ),
        );
      })
      .catch(() => {
        showToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'AlertsPage.createAlertErrorMessage',
            defaultMessage: 'An error occurred while creating the alert.',
            description: 'Error message when alert creation fails',
          }),
        );
      });
  }

  onItemUpdate(id, data) {
    const { dispatch } = this.props;

    dispatch(api.endpoints.updateAlert.initiate({ id, data }))
      .unwrap()
      .then((res) => {
        this.handleUpdateItem(res);
        showToast(
          SUCCESS,
          this.props.intl.formatMessage(
            {
              id: 'AlertsPage.alertUpdatedMessage',
              defaultMessage: 'Alert "{name}" has been updated!',
              description:
                'Success message when alert is updated. Example: name = "Contract Expiration Alert"',
            },
            {
              name: res.name,
            },
          ),
        );
      })
      .catch(() => {
        showToast(
          ERROR,
          this.props.intl.formatMessage({
            id: 'AlertsPage.updateAlertErrorMessage',
            defaultMessage: 'An error occurred while updating the alert.',
            description: 'Error message when alert update fails',
          }),
        );
      });
  }

  handleUpdateItem(alert) {
    const { id } = alert;
    const { alerts } = this.state;

    const newAlerts = [...alerts];

    const alertIndex = newAlerts.map((al) => al.id).indexOf(id);
    newAlerts[alertIndex] = { ...alert };

    this.setState({ alerts: newAlerts });
  }

  handleDeleteItems(selectedItems) {
    const newAlerts = this.state.alerts.filter(
      (alert) => !selectedItems.includes(alert.id),
    );
    this.setState({ alerts: newAlerts });
  }

  handleShowModal(modal) {
    this.setState({ currentModal: modal });
  }

  handleHideModal() {
    this.setState({ currentModal: null });
  }

  handleOnPageChange(clickedPage) {
    const selectedPage = clickedPage.selected + 1;

    this.setState({ page: selectedPage }, () => {
      this.loadAlerts();
      updatePageSearchQuery({ [QueryParamType.Page]: selectedPage }, true);
    });
  }

  handleOnFiltersChange(searchQueries) {
    this.setState({ searchQueries }, () =>
      this.handleOnPageChange({ selected: 0 }),
    );
  }

  renderAddAlertModal() {
    const { flags } = this.props;
    const enableModalConsolidation = flags[FlagType.ModalConsolidation];

    if (enableModalConsolidation) {
      return (
        <AddAlertPanel
          width="1080px"
          hideModal={this.handleHideModal}
          onItemCreate={this.onItemCreate}
        />
      );
    }

    return (
      <EcModal
        modalType={MODAL_ALERT}
        width="1080px"
        handleAlertAction={this.onItemCreate}
        hideModal={this.handleHideModal}
      />
    );
  }

  render() {
    const {
      alerts,
      page,
      pageCount,
      listLoading,
      currentModal,
      listError,
      searchQueries,
    } = this.state;

    return (
      <PageLayout
        title={this.props.intl.formatMessage({
          id: 'AlertsPage.pageTitle',
          defaultMessage: 'Alerts',
          description: 'Page title for alerts management',
        })}
        actions={[
          {
            text: this.props.intl.formatMessage({
              id: 'AlertsPage.addAlertButtonText',
              defaultMessage: 'Add Alert',
              description: 'Button text for adding new alert',
            }),
            level: 'primary',
            onClick: () => this.handleShowModal(MODAL_ALERT),
          },
        ]}
      >
        <Layout flex="auto">
          <AlertFiltersWithSideCollapse
            handleOnSearch={this.handleOnFiltersChange}
            resultsCount={!listLoading ? pageCount : null}
          />
          <AlertsList
            alerts={alerts}
            page={page}
            pageCount={pageCount}
            listLoading={listLoading}
            listError={listError}
            onItemUpdate={this.onItemUpdate}
            onItemCreate={this.onItemCreate}
            handleOnPageChange={this.handleOnPageChange}
            handleDeleteItems={this.handleDeleteItems}
            handleUpdateItem={this.handleUpdateItem}
            handleRetry={this.loadAlerts}
            searchQueries={searchQueries}
          />
        </Layout>
        {currentModal === MODAL_ALERT && this.renderAddAlertModal()}
      </PageLayout>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default withFlags(injectIntl(connect(null, mapDispatchToProps)(Page)));
